import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { NotificationService } from 'src/app/shared/services/notification.service';
import { BaseServices } from '../../../kuba.services';
import { SurveyListItemDto } from '../surveys/models';
import { WorkEnvironmentSurveyService } from '../surveys/services/work-environment-survey.service';

/**
 * The business's surveys, with create, edit and delete.
 *
 * Ported from the AMIL `UserSurvey` page and its `SurveyDataGrid`. The MUI
 * data grid and its per-row overflow menu become a `p-table` with inline
 * actions, and the bespoke delete dialog becomes the shared confirmation
 * dialog, so deleting a survey looks the same here as everywhere else.
 */
@Component({
  selector: 'survey-list',
  templateUrl: './survey-list.component.html',
  styleUrls: ['./survey-list.component.scss'],
  standalone: false
})
export class SurveyListComponent implements OnInit, OnDestroy {
  /** Raised whenever the list changes, so the survey selector can refresh. */
  @Output() surveysChanged = new EventEmitter<void>();

  //#region Variables
  private readonly notifications = inject(NotificationService);
  private readonly businessId = BaseServices.BusinessId;

  surveys: SurveyListItemDto[] = [];
  isLoading = false;

  /** Set while a row is being deleted, to disable its buttons. */
  deletingSurveyId: number | null = null;

  wizardVisible = false;
  /** Null opens the wizard for a new survey. */
  wizardSurveyId: number | null = null;

  private subscriptions: Subscription[] = [];
  //#endregion

  constructor(
    private surveyService: WorkEnvironmentSurveyService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  //#region Data
  load(): void {
    this.isLoading = true;

    this.subscriptions.push(
      this.surveyService.getList(this.businessId).subscribe({
        next: surveys => {
          this.surveys = surveys;
          this.isLoading = false;
        },
        error: () => {
          this.surveys = [];
          this.isLoading = false;
        }
      })
    );
  }
  //#endregion

  //#region Actions
  createSurvey(): void {
    this.wizardSurveyId = null;
    this.wizardVisible = true;
  }

  editSurvey(survey: SurveyListItemDto): void {
    this.wizardSurveyId = survey.Id;
    this.wizardVisible = true;
  }

  onWizardSaved(): void {
    this.wizardVisible = false;
    this.load();
    this.surveysChanged.emit();
  }

  /**
   * Opens the survey the way a respondent sees it, in a new tab.
   *
   * The respondent page lives outside the authenticated app and has its own
   * full-page layout, so it opens in a tab of its own rather than replacing
   * the module the user is working in — which is also how it is reached in
   * practice, from a link in an invitation.
   */
  openAsRespondent(survey: SurveyListItemDto): void {
    window.open(this.respondentUrl(survey.Id), '_blank');
  }

  /** Copies the respondent link, for handing out until distribution exists. */
  copyRespondentLink(survey: SurveyListItemDto): void {
    navigator.clipboard?.writeText(this.respondentUrl(survey.Id));
    this.notifications.success(
      'WORK_ENVIRONMENT_SURVEY.SURVEYS.LINK_COPIED'
    );
  }

  /**
   * Absolute respondent URL. `prepareExternalUrl` applies `APP_BASE_HREF`, so
   * the link stays correct on the platforms served under a path prefix
   * (`/ik`, `/medi3`, and the rest of `getBaseLocation`).
   */
  private respondentUrl(surveyId: number): string {
    const path = this.surveyService.getRespondentPath(surveyId);
    return `${window.location.origin}${this.location.prepareExternalUrl(path)}`;
  }

  confirmDelete(survey: SurveyListItemDto): void {
    this.confirmationService.confirm({
      message: this.translate.instant(
        'WORK_ENVIRONMENT_SURVEY.SURVEYS.DELETE_CONFIRM'
      ),
      header: this.translate.instant(
        'WORK_ENVIRONMENT_SURVEY.SURVEYS.DELETE'
      ),
      icon: 'fa fa-exclamation-triangle',
      accept: () => this.deleteSurvey(survey)
    });
  }

  private deleteSurvey(survey: SurveyListItemDto): void {
    this.deletingSurveyId = survey.Id;

    this.subscriptions.push(
      this.surveyService.delete(survey.Id).subscribe({
        next: () => {
          this.deletingSurveyId = null;
          this.notifications.success('UI_NOTIFICATIONS.DELETED');
          this.load();
          this.surveysChanged.emit();
        },
        error: () => {
          this.deletingSurveyId = null;
        }
      })
    );
  }
  //#endregion
}
